// FILE: src/pages/info/AccommodationPage.jsx
// Accommodation request before registration, no sign-in needed (MTO 12.3;
// AODA/IASR). Goes to the school's administrators; we reply by the
// requester's preferred contact.
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InfoPage from "./InfoPage";
import { requestAccommodation } from "../../services/courseInfo";
import { errorMessage } from "../../i18n/errors";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccommodationPage() {
  const [v, setV] = useState({ name: "", email: "", phone: "", needs: "", preferredContact: "EMAIL" });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { reference }
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const problems = {
    name: !v.name.trim() ? "Enter your name." : null,
    email: !EMAIL_RE.test(v.email.trim()) ? "Enter a valid email address." : null,
    phone: v.preferredContact === "PHONE" && v.phone.replace(/\D/g, "").length < 10 ? "Enter a phone number with area code." : null,
    needs: v.needs.trim().length < 10 ? "Tell us a little about what would help (at least 10 characters)." : null
  };
  const invalid = Object.values(problems).some(Boolean);

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (invalid) return;
    setBusy(true);
    try {
      const saved = await requestAccommodation({
        name: v.name.trim(),
        email: v.email.trim(),
        phone: v.phone.trim(),
        needs: v.needs.trim(),
        preferredContact: v.preferredContact
      });
      setDone({ reference: saved?.reference || null });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <InfoPage
      title="Request an accommodation"
      description="Everyone should be able to take this course. Tell us what would help: we'll contact you before you register."
    >
      <Card sx={{ maxWidth: 720 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {done ? (
            <Stack spacing={2}>
              <Alert severity="success" role="status">
                Thanks, we've received your request{done.reference ? ` (reference ${done.reference})` : ""}. We'll contact
                you within 2 business days. We've also emailed you a copy.
              </Alert>
              <Link component={RouterLink} to="/contact">
                Back to contact details
              </Link>
            </Stack>
          ) : (
            <Box component="form" onSubmit={submit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} role="alert">
                  {errorMessage(error)}
                </Alert>
              )}
              <Stack spacing={2.5}>
                <TextField label="Your name" required autoComplete="name" value={v.name} onChange={set("name")} error={touched && !!problems.name} helperText={(touched && problems.name) || " "} />
                <TextField label="Email" type="email" required autoComplete="email" inputProps={{ inputMode: "email" }} value={v.email} onChange={set("email")} error={touched && !!problems.email} helperText={(touched && problems.email) || " "} />
                <TextField label="Phone" type="tel" autoComplete="tel" inputProps={{ inputMode: "tel" }} value={v.phone} onChange={set("phone")} error={touched && !!problems.phone} helperText={(touched && problems.phone) || "Optional, unless you'd like us to call."} />
                <FormControl>
                  <FormLabel id="contact-pref-label" sx={{ fontWeight: 650, color: "text.primary" }}>
                    How should we contact you?
                  </FormLabel>
                  <RadioGroup row aria-labelledby="contact-pref-label" value={v.preferredContact} onChange={set("preferredContact")}>
                    <FormControlLabel value="EMAIL" control={<Radio />} label="Email" />
                    <FormControlLabel value="PHONE" control={<Radio />} label="Phone" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="What would help you?"
                  required
                  multiline
                  minRows={5}
                  value={v.needs}
                  onChange={set("needs")}
                  error={touched && !!problems.needs}
                  helperText={
                    (touched && problems.needs) ||
                    "For example: screen reader use, extra time, a different document format, captions, or help with sign-in steps."
                  }
                  inputProps={{ maxLength: 4000 }}
                />
                <Typography sx={{ color: "text.secondary" }}>
                  We use these details only to arrange your accommodation. See our{" "}
                  <Link component={RouterLink} to="/privacy">
                    privacy policy
                  </Link>
                  .
                </Typography>
                <Box>
                  <Button type="submit" variant="contained" size="large" disabled={busy}>
                    {busy ? <CircularProgress size={22} color="inherit" aria-label="Sending" /> : "Send request"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </InfoPage>
  );
}
